import { Component, OnInit } from '@angular/core';
import { IPageHeadline } from 'app/modules/shared/classes/page-title-parameters';
import { DisplayOptions, ISyncAsOfDateInf } from 'app/modules/shared/interfaces/display-options';
import { Router } from '@angular/router';
import { ReusableFunctions } from 'app/modules/shared/classes/reusable-functions';
import { IPositionParameters, PositionService } from '../../../shared/services/position.service';
import {
    IDataMappingProperties,
    IDataProperties,
    INavigationParameters,
    IWeightedScoreTable,
} from 'app/modules/performance/components/performance-insight-weighted-rankings/table/performance-insight-ranking-table.component';
import { IRanksParameters } from 'app/modules/performance/interfaces/performance-rankings';
import { RankingsService } from 'app/modules/performance/services/rankings.service';
import { NoDataActionType } from 'app/modules/shared/interfaces/no-data';

@Component({
    selector: 'app-performance-insight-weighted-rankings',
    templateUrl: './performance-insight-weighted-rankings.component.html',
    styleUrls: ['./performance-insight-weighted-rankings.component.scss'],
})
export class PerformanceInsightWeightedRankingsComponent implements OnInit {
    pageHeadline: IPageHeadline;
    hFilterJSON: string;
    displayOptions: DisplayOptions[];
    datePeriod: string;
    ytd: string;
    ymc: string;
    showTop: string;
    pageSize: number;
    tablePageSize: number = 10;
    leaderTables: IWeightedScoreTable[];
    syncAsOfDateInf?: ISyncAsOfDateInf;
    navigationParams: INavigationParameters;
    dataMappingProperties: IDataMappingProperties;
    dataProperties: IDataProperties;

    constructor(
        private _router: Router,
        private _reusableFunctions: ReusableFunctions,
        private _positionService: PositionService,
        private _rankingsService: RankingsService
    ) {}

    ngOnInit() {
        this.hFilterJSON = this._reusableFunctions.getHierarchyFilters();
        this.pageHeadline = {
            module: 'performance',
            moduleIcon: 'fa-heartbeat',
            moduleLink: 'performance/insights/',
            pageTitle: 'Weighted Score Leaderboard',
            linkTitle: 'Performance Insights',
        };

        this.navigationParams = {
            titleRouterBase: '/performance/insights/',
            titleUrlSegment: 'performance-rankings-position',
            cellRouterBase: '/performance/scorecards/',
            cellUrlSegment: '',
            cellQueryParamsList: [{ param: 'id', valueId: 'employeeIdentifier', source: 'cell' }],
        };

        this.dataMappingProperties = {
            unitsMappingProperty: 'totalUnits',
            volumeMappingProperty: 'totalVolume',
            scoreMappingProperty: 'avgScore',
            nameMappingProperty: 'employeeName',
        };

        this.dataProperties = {
            data: [],
            hasData: false,
            isLoading: true,
            tableTitle: '',
            roleId: '',
            tablePageSize: this.tablePageSize,
            showFooter: false,
            action: NoDataActionType.pageStart,
        };

        this.displayOptions = this._reusableFunctions.getCurrentDisplayOptions(this._router.url);
        this.setDisplayOptionsSelected();
        this.getPositions();
    }

    getPositions() {
        let parameters: IPositionParameters = {
            ymc: this.ymc,
            fromDatePeriod: this.datePeriod,
            getEmployeeAssignned: '1',
        };

        this.leaderTables = [];
        this._positionService.getScorecardPositions(parameters).subscribe(
            (res) => {
                res.forEach((card, index) => {
                    let dataProperties = this.dataProperties;
                    let navigationParams = this.navigationParams;
                    dataProperties.roleId = card.positionIdentifier.toString();
                    dataProperties.tableTitle = card.position;
                    dataProperties.tablePageSize = this.tablePageSize;
                    navigationParams.cellUrlSegment = card.scoreCardUrlSegment;

                    navigationParams.cellQueryParamsList = navigationParams.cellQueryParamsList.map(
                        (obj) => ({ ...obj })
                    );

                    let scoreTable: IWeightedScoreTable = Object.assign(
                        {},
                        {
                            dataProperties: Object.assign({}, dataProperties),
                            navigationParams: Object.assign({}, navigationParams),
                            dataMappingProperties: Object.assign({}, this.dataMappingProperties),
                        }
                    );
                    this.leaderTables.push(scoreTable);
                });
                this.getRankings();
            },
            (error) => {
                throw new Error(error);
            }
        );
    }

    getRankings() {
        let parameters: IRanksParameters = {
            datePeriod: this.datePeriod,
            entityType: '',
            entityCode: '',
            hFilterJSON: this.hFilterJSON,
            ymc: this.ymc,
        };

        this.leaderTables.forEach((scoreTable: IWeightedScoreTable) => {
            parameters.entityCode = scoreTable.dataProperties.roleId.toString();
            this._rankingsService.getWeightedScoreByPosition(parameters).subscribe(
                (data) => {
                    if (data && data.length > 0) {
                        scoreTable.dataProperties.data = data;
                        scoreTable.dataProperties.hasData = true;
                    } else {
                        scoreTable.dataProperties.hasData = false;
                    }
                    scoreTable.dataProperties.isLoading = false;
                    scoreTable.dataProperties = Object.assign({}, scoreTable.dataProperties);
                    scoreTable = Object.assign({}, scoreTable);
                },
                (error) => {
                    scoreTable.dataProperties.hasData = false;
                    scoreTable.dataProperties.isLoading = false;
                    scoreTable.dataProperties.action = NoDataActionType.error;
                    throw new Error(error);
                }
            );
        });
    }

    applyNewFilters(event: string) {
        this.hFilterJSON = event;
        this.getPositions();
    }

    setDisplayOptionsSelected() {
        let asOfDateId = 'scorecardAsOf';
        let dateRangeId = 'dateRangeRolling_12m_ytd_lm-mtd';
        this.syncAsOfDateInf = {
            asOfDateId: asOfDateId,
            syncWithDisplayOptionId: dateRangeId,
        };

        let asOfInfo = this.displayOptions.find((item) => item.optionKey === asOfDateId);
        if (asOfInfo) {
            this.ymc = asOfInfo.selectedOptions;
        }

        let dateRangeInfo = this.displayOptions.find((item) => item.optionKey === dateRangeId);
        if (dateRangeInfo) {
            this.datePeriod = dateRangeInfo.selectedOptions;
        }

        let showTopInfo = this.displayOptions.find((item) => item.optionKey === 'showTop');
        if (showTopInfo) {
            this.showTop = showTopInfo.selectedOptions;
            this.assignGroupBy();
        }
    }

    assignGroupBy(): void {
        let topNumber = this.showTop.replace('Top ', '');
        this.tablePageSize = +topNumber;
    }

    applyDisplayOptions(event: DisplayOptions[]) {
        this.displayOptions = event;
        this.setDisplayOptionsSelected();
        this.getPositions();
    }
}
